import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useForm, useFieldArray } from "react-hook-form";
import { Toaster, toast } from "react-hot-toast";
import { uploadImagecertifates } from "../../../Redux/Actions/Action";
import { validateFile } from "../../../utils/comman-function";
import { IoIosAddCircle, IoIosCloseCircle, IoIosCloudUpload } from "react-icons/io";

const BASE_URL = process.env.REACT_APP_BASEURL;

const ReferralScreenConfig = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const { register, control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            mainTitle: "",
            subTitle: "",
            mainIcon: "",
            referralCodeLabel: "",
            steps: [
                { order: 1, text: "", icon: "" },
                { order: 2, text: "", icon: "" },
                { order: 3, text: "", icon: "" }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "steps"
    });

    const watchMainIcon = watch("mainIcon");
    const watchSteps = watch("steps");

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("astroVani-admin-token");
            const { data } = await axios.get(`${BASE_URL}/admin/referralScreenConfig`, {
                headers: { Authorization: token }
            });

            if (data?.data) {
                const { mainTitle, subTitle, mainIcon, referralCodeLabel, steps } = data.data;
                setValue("mainTitle", mainTitle);
                setValue("subTitle", subTitle);
                setValue("mainIcon", mainIcon);
                setValue("referralCodeLabel", referralCodeLabel);

                // If steps exist, update them, otherwise keep defaults
                if (steps && steps.length > 0) {
                    setValue("steps", steps);
                }
            }
        } catch (error) {
            console.error("Error fetching referral config:", error);
            toast.error("Failed to load configuration");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e, fieldName, index = null) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validateFile(file, 'image')) {
            return;
        }

        try {
            const toastId = toast.loading("Uploading image...");
            const uploadedUrl = await dispatch(uploadImagecertifates(file));
            toast.dismiss(toastId);

            if (uploadedUrl) {
                if (index !== null) {
                    setValue(`steps.${index}.icon`, uploadedUrl[0]);
                } else {
                    setValue(fieldName, uploadedUrl[0]);
                }
                toast.success("Image uploaded successfully");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Failed to upload image");
        }
    };

    const onSubmit = async (formData) => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem("astroVani-admin-token");

            // Ensure order is correct
            const payload = {
                ...formData,
                steps: formData.steps.map((step, idx) => ({
                    ...step,
                    order: idx + 1
                }))
            };

            const { data } = await axios.patch(`${BASE_URL}/admin/referralScreenConfig`, payload, {
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json'
                }
            });

            if (data?.success) {
                toast.success("Configuration updated successfully!");
                fetchConfig(); // Refresh data
            } else {
                toast.error(data?.message || "Update failed");
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error(error?.response?.data?.message || "Failed to update configuration");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm border-0 mb-4">
            <Toaster position="top-right" />
            <div className="card-header bg-white py-3">
                <h5 className="mb-0 fw-bold text-primary">Referral Screen Configuration</h5>
                <p className="text-muted small mb-0">Manage the "Refer & Earn" screen content in the user app.</p>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Main Section */}
                    <div className="row g-4 mb-4">
                        <div className="col-md-8">
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Main Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Invite Friends & Earn ₹50"
                                    {...register("mainTitle", { required: "Main title is required" })}
                                />
                                {errors.mainTitle && <small className="text-danger">{errors.mainTitle.message}</small>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Subtitle</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Share the wisdom of astrology..."
                                    {...register("subTitle", { required: "Subtitle is required" })}
                                />
                                {errors.subTitle && <small className="text-danger">{errors.subTitle.message}</small>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Referral Code Label</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Your referral code"
                                    {...register("referralCodeLabel", { required: "Label is required" })}
                                />
                                {errors.referralCodeLabel && <small className="text-danger">{errors.referralCodeLabel.message}</small>}
                            </div>
                        </div>

                        {/* Main Icon Upload */}
                        <div className="col-md-4">
                            <label className="form-label fw-semibold d-block">Main Illustration</label>
                            <div className="border rounded p-3 text-center bg-light position-relative"
                                style={{ height: "200px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                {watchMainIcon ? (
                                    <>
                                        <img src={watchMainIcon} alt="Main Icon" style={{ maxHeight: "150px", maxWidth: "100%", objectFit: "contain" }} />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle p-1"
                                            onClick={() => setValue("mainIcon", "")}
                                            style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        >
                                            <IoIosCloseCircle size={20} />
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-muted">
                                        <IoIosCloudUpload size={40} className="mb-2 text-primary opacity-50" />
                                        <p className="small mb-2">Upload Main Illustration</p>
                                        <label className="btn btn-sm btn-outline-primary">
                                            Choose File
                                            <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(e, "mainIcon")} />
                                        </label>
                                    </div>
                                )}
                            </div>
                            <input type="hidden" {...register("mainIcon", { required: "Main icon is required" })} />
                            {errors.mainIcon && <small className="text-danger d-block mt-1">{errors.mainIcon.message}</small>}
                        </div>
                    </div>

                    <hr className="my-4" />

                    {/* Steps Section */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0">How it Works - Steps</h6>
                            {/* <button type="button" className="btn btn-sm btn-light text-primary fw-semibold" onClick={() => append({ order: fields.length + 1, text: "", icon: "" })}>
                                <IoIosAddCircle size={18} className="me-1" /> Add Step
                            </button> */}
                        </div>

                        <div className="row g-3">
                            {fields.map((field, index) => (
                                <div key={field.id} className="col-md-4">
                                    <div className="card h-100 border shadow-sm">
                                        <div className="card-header bg-transparent d-flex justify-content-between align-items-center py-2">
                                            <span className="badge bg-primary rounded-pill">Step {index + 1}</span>
                                            {/* {fields.length > 1 && (
                                                <button type="button" className="btn btn-link text-danger p-0" onClick={() => remove(index)}>
                                                    <IoIosCloseCircle size={20} />
                                                </button>
                                            )} */}
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-3 text-center">
                                                <div className="border rounded p-2 bg-light d-inline-block position-relative" style={{ width: "80px", height: "80px" }}>
                                                    {watchSteps[index]?.icon ? (
                                                        <img
                                                            src={watchSteps[index].icon}
                                                            alt={`Step ${index + 1}`}
                                                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                        />
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                                            <IoIosCloudUpload size={24} />
                                                        </div>
                                                    )}
                                                    <label className="position-absolute w-100 h-100 top-0 start-0" style={{ cursor: "pointer" }}>
                                                        <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(e, null, index)} />
                                                    </label>
                                                </div>
                                                <div className="small text-muted mt-1">Click to upload icon</div>
                                            </div>

                                            <div className="mb-2">
                                                <label className="form-label small fw-semibold">Description Text</label>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    rows="2"
                                                    placeholder="Step description..."
                                                    {...register(`steps.${index}.text`, { required: "Text is required" })}
                                                ></textarea>
                                                {errors.steps?.[index]?.text && <small className="text-danger">{errors.steps[index].text.message}</small>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <button type="submit" className="btn btn-primary px-4 py-2 fw-semibold shadow-sm" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving Changes...
                                </>
                            ) : (
                                "Save Configuration"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReferralScreenConfig;
